// src/components/my-journey/MyJourney.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useLoaderData } from 'react-router-dom';
import {
  ArrowRight, BarChart2, ChevronLeft, ArrowLeftRight, User, TrendingUp, Ghost, Heart,
} from 'lucide-react';
import { useLeagueData, useManagerContext } from '../../context/DataContext';
import ManagerProfile from '../profile/ManagerProfile';
import FPLLogo from '../shared/FPLLogo';
import SeasonStoryDeck from './SeasonStoryDeck';
import RankPressureWave from './RankPressureWave';
import BenchHaunting from './BenchHaunting';
import SquadLifecycle from './SquadLifecycle';


const MyJourney = () => {
  const location = useLocation();
  const loaderData = useLoaderData();
  const [activeTab, setActiveTab] = useState(
    (location.state?.activeTab || loaderData?.activeTab) || 'profile'
  );
  const [hoveredGW, setHoveredGW] = useState(null);
  const [isStoryOpen, setIsStoryOpen] = useState(false);
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const pageRef = useRef(null);

  const { data } = useLeagueData();
  const { selectedManager } = useManagerContext();

  const updateActiveTab = (tab) => {
    setActiveTab(tab);
    window.history.replaceState({ activeTab: tab }, '', location.pathname);
    window.dispatchEvent(new CustomEvent('fpl-tab-changed', { detail: { tab, path: location.pathname } }));
  };

  useEffect(() => {
    if (pageRef.current) pageRef.current.scrollTop = 0;
    if (activeTab === 'performance' && scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [activeTab]);

  useEffect(() => {
    if (location.state?.activeTab) setActiveTab(location.state.activeTab);
    else if (loaderData?.activeTab) setActiveTab(loaderData.activeTab);
  }, [location.state, loaderData]);

  // ── Performance Tab ────────────────────────────────────────────────────────
  const renderPerformanceTab = () => {
    if (!selectedManager) {
      return (
        <div className="rounded-xl p-6 text-center" style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}>
          <h3 className="text-lg font-bold mb-2">No manager selected</h3>
          <p className="text-sm" style={{ color: 'var(--c-grey)' }}>Please select a manager to view their performance journey.</p>
        </div>
      );
    }

    let gameweekPerformance = [];

    if (data?.performanceVsAverage?.managers?.[selectedManager]) {
      gameweekPerformance = data.performanceVsAverage.managers[selectedManager];
    } else if (data?.weeklyPoints?.length > 0) {
      const gameweekData = prepareGameweekData(data.weeklyPoints);
      if (!gameweekData.scores[selectedManager]) {
        return (
          <div className="rounded-xl p-6 text-center" style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}>
            <h3 className="text-lg font-bold mb-2">No data found</h3>
            <p className="text-sm" style={{ color: 'var(--c-grey)' }}>Unable to find gameweek data for {selectedManager}.</p>
          </div>
        );
      }

      const gameweeks = Object.keys(gameweekData.scores[selectedManager]).map(Number).sort((a, b) => a - b);
      gameweekPerformance = gameweeks.map((gw) => {
        const userScore = Math.round(gameweekData.scores[selectedManager][gw]);
        const avgScore = Math.round(gameweekData.averages[gw] || 0);
        const diff = userScore - avgScore;
        return { gw, userScore, avgScore, diff, aboveAverage: diff > 0 };
      });
    } else {
      return (
        <div className="rounded-xl p-6 text-center" style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}>
          <h3 className="text-lg font-bold mb-2">No data available</h3>
          <p className="text-sm" style={{ color: 'var(--c-grey)' }}>Unable to generate the performance journey.</p>
        </div>
      );
    }

    gameweekPerformance.forEach((gw) => {
      gw.userScore = Math.round(gw.userScore);
      gw.avgScore = Math.round(gw.avgScore);
      gw.diff = gw.userScore - gw.avgScore;
      gw.aboveAverage = gw.diff > 0;
    });

    const topGWs = [...gameweekPerformance].sort((a, b) => b.diff - a.diff).slice(0, 5);
    const bottomGWs = [...gameweekPerformance].sort((a, b) => a.diff - b.diff).slice(0, 5);
    const highlightedGWs = new Set([...topGWs.map((g) => g.gw), ...bottomGWs.map((g) => g.gw)]);

    let bestGW = { gw: 0, score: 0 };
    let worstGW = { gw: 0, score: Infinity };
    let aboveAverage = 0, belowAverage = 0;

    gameweekPerformance.forEach((gw) => {
      if (gw.userScore > bestGW.score) bestGW = { gw: gw.gw, score: gw.userScore };
      if (gw.userScore < worstGW.score) worstGW = { gw: gw.gw, score: gw.userScore };
      if (gw.diff > 0) aboveAverage++;
      else if (gw.diff < 0) belowAverage++;
    });

    const maxDiff = Math.max(...gameweekPerformance.map((gw) => Math.abs(gw.diff)));
    const getBarWidth = (diff) => Math.min((Math.abs(diff) / maxDiff) * 100, 100);

    return (
      <div className="space-y-4">
        {/* Summary stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Best Gameweek', value: `${bestGW.score} pts`, sub: `GW${bestGW.gw}`, colour: 'var(--c-turquoise)' },
            { label: 'Worst Gameweek', value: `${worstGW.score} pts`, sub: `GW${worstGW.gw}`, colour: 'var(--c-coral)' },
            { label: 'Above Average', value: `${aboveAverage}`, sub: 'gameweeks', colour: 'var(--c-good)' },
            { label: 'Below Average', value: `${belowAverage}`, sub: 'gameweeks', colour: 'var(--c-bad)' },
          ].map(({ label, value, sub, colour }) => (
            <div key={label} className="p-4 rounded-xl animate-pop-in"
              style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}>
              <p className="text-xs mb-1" style={{ color: 'var(--c-grey)' }}>{label}</p>
              <p className="text-xl font-bold" style={{ color: colour }}>{value}</p>
              <p className="text-xs" style={{ color: 'var(--c-grey)' }}>{sub}</p>
            </div>
          ))}
        </div>

        {/* Gameweek journey chart */}
        <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}>
          <h3 className="font-bold text-base mb-1 flex items-center gap-2">
            <BarChart2 size={18} style={{ color: 'var(--c-blue)' }} />
            Gameweek Journey
          </h3>
          <p style={{ fontSize: '12px', color: 'var(--c-grey)', marginBottom: '12px' }}>
            Weekly points compared to the league average — green bars are above average, red below.
          </p>
          <p className="text-xs mb-4 flex items-center gap-1" style={{ color: 'var(--c-grey)' }}>
            <ArrowLeftRight size={13} />
            Scroll to view your full season
          </p>

          {/* Legend */}
          <div className="flex items-center gap-4 text-xs mb-3 px-3 py-2 rounded-lg"
            style={{ backgroundColor: 'var(--c-surface-3)' }}>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--c-good)' }} />
              <span style={{ color: 'var(--c-grey)' }}>Above avg</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--c-bad)' }} />
              <span style={{ color: 'var(--c-grey)' }}>Below avg</span>
            </span>
            <span className="c-badge q-badge-good ml-auto">Top 5</span>
            <span className="c-badge q-badge-bad">Bottom 5</span>
          </div>

          {/* Scrollable chart */}
          <div
            ref={scrollRef}
            className="overflow-y-auto max-h-96"
            style={{ scrollbarWidth: 'thin', scrollbarColor: 'var(--c-blue) var(--c-surface-3)' }}
          >
            {/* Column headers */}
            <div
              className="flex mb-1 text-xs font-medium sticky top-0 py-2 z-10"
              style={{ backgroundColor: 'var(--c-surface-2)', color: 'var(--c-grey)' }}
            >
              <div className="w-14 flex-shrink-0 pl-2">Week</div>
              <div className="w-16 flex-shrink-0 text-right pr-2">Score</div>
              <div className="w-[40%] text-right pr-2" style={{ color: 'var(--c-bad)' }}>Below Avg</div>
              <div className="w-8 text-center">Avg</div>
              <div className="w-[40%] pl-2" style={{ color: 'var(--c-good)' }}>Above Avg</div>
              <div className="w-20 flex-shrink-0" />
            </div>

            {gameweekPerformance.map((gw) => {
              const isTop5 = topGWs.some((g) => g.gw === gw.gw);
              const isBottom5 = bottomGWs.some((g) => g.gw === gw.gw);
              const isHighlighted = highlightedGWs.has(gw.gw);

              return (
                <div
                  key={gw.gw}
                  className="flex items-center py-2 transition-colors cursor-default"
                  style={{
                    backgroundColor:
                      hoveredGW === gw.gw
                        ? 'rgba(255, 255, 255, 0.05)'
                        : isHighlighted
                        ? 'rgba(255, 255, 255, 0.025)'
                        : 'transparent',
                  }}
                  onMouseEnter={() => setHoveredGW(gw.gw)}
                  onMouseLeave={() => setHoveredGW(null)}
                >
                  <div className="w-14 flex-shrink-0 pl-2 text-xs font-medium" style={{ color: 'var(--c-grey)' }}>
                    GW{gw.gw}
                  </div>
                  <div className="w-16 flex-shrink-0 text-right pr-2 text-xs font-medium text-white">
                    {gw.userScore}
                  </div>

                  {/* Below avg bar */}
                  <div className="w-[40%] flex justify-end">
                    {!gw.aboveAverage && gw.diff !== 0 && (
                      <div
                        className="h-6 flex items-center justify-end pr-1.5 rounded-l-md text-xs font-medium text-white"
                        style={{ width: `${getBarWidth(gw.diff)}%`, backgroundColor: 'var(--c-bad)' }}
                      >
                        {gw.diff}
                      </div>
                    )}
                  </div>

                  {/* Centre */}
                  <div className="w-8 flex justify-center relative">
                    <div className="absolute w-px h-full" style={{ backgroundColor: 'var(--c-border)' }} />
                    <div
                      className="w-3 h-3 rounded-full z-10"
                      style={{
                        backgroundColor:
                          gw.diff > 0 ? 'var(--c-good)' : gw.diff < 0 ? 'var(--c-bad)' : 'var(--c-grey)',
                        outline: isHighlighted ? '2px solid rgba(255, 255, 255, 0.3)' : 'none',
                        outlineOffset: '1px',
                      }}
                    />
                    <div className="absolute -bottom-4 text-xs" style={{ color: 'var(--c-grey)' }}>
                      {gw.avgScore}
                    </div>
                  </div>

                  {/* Above avg bar */}
                  <div className="w-[40%]">
                    {gw.aboveAverage && gw.diff !== 0 && (
                      <div
                        className="h-6 flex items-center pl-1.5 rounded-r-md text-xs font-medium text-white"
                        style={{ width: `${getBarWidth(gw.diff)}%`, backgroundColor: 'var(--c-good)' }}
                      >
                        +{gw.diff}
                      </div>
                    )}
                  </div>

                  {/* Badge */}
                  <div className="w-20 flex-shrink-0 pl-1">
                    {isTop5 && <span className="c-badge q-badge-good text-xs whitespace-nowrap">Top 5</span>}
                    {isBottom5 && <span className="c-badge q-badge-bad text-xs whitespace-nowrap">Bottom 5</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top & bottom summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid rgba(33, 150, 83, 0.3)', borderLeft: '3px solid var(--c-good)' }}>
            <h4 className="font-semibold text-sm mb-3" style={{ color: 'var(--c-turquoise)' }}>Top 5 Gameweeks vs Average</h4>
            <div className="space-y-2">
              {topGWs.map((item, i) => (
                <div
                  key={i}
                  className="flex justify-between items-center px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: hoveredGW === item.gw ? 'rgba(33, 150, 83, 0.15)' : 'var(--c-surface-3)',
                    border: hoveredGW === item.gw ? '1px solid rgba(33, 150, 83, 0.4)' : '1px solid transparent',
                  }}
                  onMouseEnter={() => setHoveredGW(item.gw)}
                  onMouseLeave={() => setHoveredGW(null)}
                >
                  <span className="font-medium text-white">GW{item.gw}</span>
                  <div className="flex items-center gap-2">
                    <span style={{ color: 'var(--c-grey)' }}>{item.userScore} pts</span>
                    <span className="font-semibold" style={{ color: 'var(--c-good)' }}>+{item.diff}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid rgba(235, 87, 87, 0.3)', borderLeft: '3px solid var(--c-bad)' }}>
            <h4 className="font-semibold text-sm mb-3" style={{ color: 'var(--c-coral)' }}>Bottom 5 Gameweeks vs Average</h4>
            <div className="space-y-2">
              {bottomGWs.map((item, i) => (
                <div
                  key={i}
                  className="flex justify-between items-center px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: hoveredGW === item.gw ? 'rgba(235, 87, 87, 0.1)' : 'var(--c-surface-3)',
                    border: hoveredGW === item.gw ? '1px solid rgba(235, 87, 87, 0.4)' : '1px solid transparent',
                  }}
                  onMouseEnter={() => setHoveredGW(item.gw)}
                  onMouseLeave={() => setHoveredGW(null)}
                >
                  <span className="font-medium text-white">GW{item.gw}</span>
                  <div className="flex items-center gap-2">
                    <span style={{ color: 'var(--c-grey)' }}>{item.userScore} pts</span>
                    <span className="font-semibold" style={{ color: 'var(--c-bad)' }}>{item.diff}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    );
  };

  // Helper function to prepare gameweek data from weekly points
  const prepareGameweekData = (weeklyPointsData) => {
    const scores = {};
    const averages = {};
    const gameweekKeys = Object.keys(weeklyPointsData[0] || {})
      .filter((key) => !isNaN(parseInt(key)))
      .map(Number)
      .sort((a, b) => a - b);

    weeklyPointsData.forEach((manager) => { scores[manager.Manager] = {}; });

    gameweekKeys.forEach((gw) => {
      let totalPoints = 0, managerCount = 0;
      weeklyPointsData.forEach((manager) => {
        const gwScore = parseInt(manager[gw]);
        if (!isNaN(gwScore)) {
          scores[manager.Manager][gw] = gwScore;
          totalPoints += gwScore;
          managerCount++;
        }
      });
      if (managerCount > 0) averages[gw] = Math.round(totalPoints / managerCount);
    });

    return { scores, averages };
  };

  // ── Tabs config ────────────────────────────────────────────────────────────
  const tabs = [
    { id: 'profile',       label: 'My Profile',     icon: <User size={15} /> },
    { id: 'performance',   label: 'Performance',    icon: <BarChart2 size={15} /> },
    { id: 'rankpressure',  label: 'Rank Pressure',  icon: <TrendingUp size={15} /> },
    { id: 'benchhaunting', label: 'Points Haunting', icon: <Ghost size={15} /> },
    { id: 'squadstory',    label: 'Squad Story',    icon: <Heart size={15} /> },
  ];

  return (
    <div
      ref={pageRef}
      className="w-full h-full overflow-auto text-white"
      style={{ background: 'linear-gradient(135deg, #000006 0%, #0a1628 100%)' }}
    >
      {/* Header bar */}
      <div
        className="sticky top-0 z-20 px-6 py-3 flex items-center justify-between"
        style={{ backgroundColor: 'rgba(0, 0, 6, 0.9)', backdropFilter: 'blur(12px)', borderBottom: '1px solid var(--c-border)' }}
      >
        <div className="flex items-center gap-3">
          <FPLLogo size="icon" colour="light" className="h-6" />
          <div>
            <p className="text-xs font-medium text-white">{selectedManager}</p>
            <p className="text-xs" style={{ color: 'var(--c-grey)' }}>FPL Journey · 2025/26</p>
          </div>
        </div>

        {/* Tab switcher */}
        <div
          className="flex p-1 rounded-full gap-1"
          style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => updateActiveTab(tab.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all"
              style={
                activeTab === tab.id
                  ? { backgroundColor: 'var(--c-blue)', color: 'white' }
                  : { color: 'var(--c-grey)' }
              }
            >
              {tab.icon}
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Full-page feature tabs (no max-width container) */}
      {activeTab === 'rankpressure' && (
        <>
          <RankPressureWave />
          <div className="flex justify-between px-6 py-4" style={{ borderTop: '1px solid var(--c-border)' }}>
            <button onClick={() => updateActiveTab('performance')} className="flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all"
              style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid var(--c-border)', color: 'var(--c-grey)' }}>
              <ChevronLeft size={16} /> Performance
            </button>
            <button onClick={() => updateActiveTab('benchhaunting')} className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white transition-all"
              style={{ backgroundColor: 'var(--c-blue)' }}>
              Points Haunting <ArrowRight size={14} />
            </button>
          </div>
        </>
      )}
      {activeTab === 'benchhaunting' && (
        <>
          <BenchHaunting />
          <div className="flex justify-between px-6 py-4" style={{ borderTop: '1px solid var(--c-border)' }}>
            <button onClick={() => updateActiveTab('rankpressure')} className="flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all"
              style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid var(--c-border)', color: 'var(--c-grey)' }}>
              <ChevronLeft size={16} /> Rank Pressure
            </button>
            <button onClick={() => updateActiveTab('squadstory')} className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white transition-all"
              style={{ backgroundColor: 'var(--c-blue)' }}>
              Squad Story <ArrowRight size={14} />
            </button>
          </div>
        </>
      )}
      {activeTab === 'squadstory' && (
        <>
          <SquadLifecycle />
          <div className="flex justify-between px-6 py-4" style={{ borderTop: '1px solid var(--c-border)' }}>
            <button onClick={() => updateActiveTab('benchhaunting')} className="flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all"
              style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid var(--c-border)', color: 'var(--c-grey)' }}>
              <ChevronLeft size={16} /> Points Haunting
            </button>
            <button onClick={() => navigate('/league-overview')} className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white transition-all"
              style={{ backgroundColor: 'var(--c-blue)' }}>
              League Overview <ArrowRight size={14} />
            </button>
          </div>
        </>
      )}

      {/* Contained content tabs */}
      {(activeTab === 'performance' || activeTab === 'profile') && (
        <div className="max-w-3xl mx-auto px-6 py-6 pb-8">
          {activeTab === 'performance' && renderPerformanceTab()}
          {activeTab === 'profile' && (
            <>
              {selectedManager && (
                <button
                  onClick={() => setIsStoryOpen(true)}
                  className="w-full py-3 rounded-full font-semibold text-sm text-white mb-4"
                  style={{
                    background: 'linear-gradient(135deg, var(--c-blue) 0%, var(--c-turquoise) 100%)',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  Watch Your Season Story
                </button>
              )}
              <ManagerProfile />
            </>
          )}

          {/* Navigation buttons */}
          <div className="flex justify-between mt-8 pt-6" style={{ borderTop: '1px solid var(--c-border)' }}>
            <button
              onClick={() => navigate('/journey-selector', { state: { forceJourneySelection: true } })}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all"
              style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid var(--c-border)', color: 'var(--c-grey)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'white'; e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--c-grey)'; e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; }}
            >
              <ChevronLeft size={16} />
              Back
            </button>
            <div className="flex gap-3">
              {activeTab === 'profile' && (
                <button
                  onClick={() => updateActiveTab('performance')}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white transition-all"
                  style={{ backgroundColor: 'var(--c-blue)' }}
                >
                  Performance <ArrowRight size={14} />
                </button>
              )}
              {activeTab === 'performance' && (
                <button
                  onClick={() => updateActiveTab('rankpressure')}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white transition-all"
                  style={{ backgroundColor: 'var(--c-blue)' }}
                >
                  Rank Pressure <ArrowRight size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Season Story Deck overlay */}
      <SeasonStoryDeck isOpen={isStoryOpen} onClose={() => setIsStoryOpen(false)} />
    </div>
  );
};

export default MyJourney;
